/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type {
  AiQueryOidcMessageTarget,
  RunAiQueryOidcHandshakeOptions,
} from './types';
import {
  AI_QUERY_BRIDGE_HANDSHAKE_MS,
  AI_QUERY_BRIDGE_MSG_READY,
  AI_QUERY_BRIDGE_MSG_TOKEN,
} from './constants';

function normalizeOrigin(origin: string): string {
  try {
    return new URL(origin).origin;
  } catch {
    return origin.replace(/\/$/, '');
  }
}

function originsMatch(received: string, expected: string): boolean {
  return normalizeOrigin(received) === normalizeOrigin(expected);
}

function resolveMessageTarget(
  ev: MessageEvent,
  getMessageTarget: () => AiQueryOidcMessageTarget | null,
): AiQueryOidcMessageTarget | null {
  const { source } = ev;
  if (
    source &&
    typeof (source as AiQueryOidcMessageTarget).postMessage === 'function'
  ) {
    return source as AiQueryOidcMessageTarget;
  }
  return getMessageTarget();
}

/**
 * 监听 IdP 桥接页 postMessage，向子窗口/iframe 下发 userkey。
 * 对每次 bridge-ready 尝试下发，直至成功或超时（兼容 iframe 晚就绪、多次重试）。
 * 返回 dispose，调用方须在组件卸载或超时后执行。
 */
export function runAiQueryOidcHandshake(
  options: RunAiQueryOidcHandshakeOptions,
): () => void {
  const {
    idpOrigin,
    token,
    getMessageTarget,
    handshakeMs = AI_QUERY_BRIDGE_HANDSHAKE_MS,
    onHandshakeComplete,
    onTimeout,
  } = options;

  const expectedOrigin = normalizeOrigin(idpOrigin);
  let cleaned = false;
  let tokenDelivered = false;

  const onMessage = (ev: MessageEvent) => {
    if (!originsMatch(ev.origin, expectedOrigin)) {
      return;
    }
    if (ev.data?.type !== AI_QUERY_BRIDGE_MSG_READY) {
      return;
    }
    if (tokenDelivered) {
      return;
    }

    const target = resolveMessageTarget(ev, getMessageTarget);
    if (!target) {
      return;
    }

    target.postMessage({ type: AI_QUERY_BRIDGE_MSG_TOKEN, token }, ev.origin);
    tokenDelivered = true;
    cleanup();
    onHandshakeComplete?.();
  };

  const timer = window.setTimeout(() => {
    cleanup();
    if (!tokenDelivered) {
      onTimeout?.();
    }
  }, handshakeMs);

  function cleanup() {
    if (cleaned) {
      return;
    }
    cleaned = true;
    window.removeEventListener('message', onMessage);
    window.clearTimeout(timer);
  }

  window.addEventListener('message', onMessage);
  return cleanup;
}
